import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  Snackbar,
  AppBar,
  Toolbar,
  Button
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../components/ImageUpload';
import { uploadAndAnalyze } from '../api/imageQuality';

export default function ImageQuality() {
  const navigate = useNavigate();
  const [uploadedSuccessfully, setUploadedSuccessfully] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleUploadSuccess = async (file, metadata, onProgress) => {
    try {
      setError(null);
      setUploadedSuccessfully(false);

      const result = await uploadAndAnalyze(file, metadata, onProgress);

      if (result.success) {
        setUploadedSuccessfully(true);
        setSnackbarOpen(true);
      } else {
        throw new Error(result.error || 'Analysis failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Upload failed';
      setError(errorMessage);
      throw err;
    }
  };

  const handleUploadError = (err) => {
    const errorMessage = err.response?.data?.error || err.message || 'Upload failed';
    setError(errorMessage);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Button
            color="inherit"
            startIcon={<BackIcon />}
            onClick={() => navigate('/')}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <PhotoIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Product Photo Quality Checker
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Analyze Product Photo Quality
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Upload a product photo to analyze its quality for social media posting. 
            We'll check brightness, sharpness, contrast, and product centering using AWS Rekognition.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <ImageUpload
            onUploadSuccess={handleUploadSuccess}
            onUploadError={handleUploadError}
          />

          {uploadedSuccessfully && (
            <Alert severity="success" sx={{ mt: 3 }} icon={false}>
              <strong>Image uploaded successfully!</strong><br />
              Quality analysis (brightness, sharpness, contrast, centering) and frame shape detection will be processed tonight during the nightly batch. Check back tomorrow or open the image in Image Quality records to view results.
            </Alert>
          )}
        </Box>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Image uploaded successfully! Analysis queued for tonight.
        </Alert>
      </Snackbar>
    </>
  );
}
